import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { formatPhoneInput } from "../../utils/phoneUtils";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { 
  Plus, Edit, Trash2, Phone, Mail, UserCircle, DollarSign, 
  Percent, CreditCard, Briefcase, Send, Loader2, Wrench, CheckCircle, Clock, Lock, PhoneOutgoing, MessageSquare,
  ChevronDown, ChevronUp, MapPin, AlertTriangle, ShieldCheck, Users
} from "lucide-react";
import { API, AuthContext } from "../../App";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";
import { usePlanLimits } from "../../contexts/PlanLimitContext";
import TechPermissionsTab, { PermissionToggleGrid } from "../../components/technicians/TechPermissionsTab";

const TechniciansPage = () => {
  const { isDark } = useTheme();
  const { isSmsReadyAsync } = usePlanLimits();
  const { user } = useContext(AuthContext);
  const isStoreAccount = user?.account_type === "multi_location_store";
  const [technicians, setTechnicians] = useState([]);
  const [storeLocations, setStoreLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedServices, setExpandedServices] = useState({}); // { [tech_id]: bool }
  const [editingTechnician, setEditingTechnician] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [sendingInvite, setSendingInvite] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [unapprovedJobs, setUnapprovedJobs] = useState([]);
  const [loadingUnapproved, setLoadingUnapproved] = useState(false);
  const [fixingJobs, setFixingJobs] = useState(false);
  
  // Settings data for financial options
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [jobSources, setJobSources] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [maskingNumbers, setMaskingNumbers] = useState([]);
  const [pageTab, setPageTab] = useState("technicians");
  const [defaultPerms, setDefaultPerms] = useState({});
  const [permLabels, setPermLabels] = useState({});
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    services: [],
    commission_rate: "",
    parts_deduction: "none",
    charge_processing_fees: true,
    processing_fees: {},
    commission_by_source: {},
    commission_by_service: {},
    call_masking_number: "",
    custom_assignment_template: "",
    location_ids: [],
    permissions: null,
  });

  const toggleService = (serviceId) => {
    if (!serviceId) return;
    const current = formData.services;
    if (current.includes(serviceId)) {
      setFormData({ ...formData, services: current.filter(s => s !== serviceId) });
    } else {
      setFormData({ ...formData, services: [...current, serviceId] });
    }
  };

  const toggleLocation = (locationId) => {
    if (!locationId) return;
    const current = formData.location_ids;
    if (current.includes(locationId)) {
      setFormData({ ...formData, location_ids: current.filter(l => l !== locationId) });
    } else {
      setFormData({ ...formData, location_ids: [...current, locationId] });
    }
  };

  useEffect(() => {
    fetchTechnicians();
    fetchSettingsData();
  }, []);

  const fetchSettingsData = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
      
      const [paymentRes, sourcesRes, servicesRes, maskingRes, locationsRes, permsRes] = await Promise.all([
        axios.get(`${API}/settings/payment-types`, config),
        axios.get(`${API}/settings/job-sources`, config),
        axios.get(`${API}/settings/job-types`, config),
        axios.get(`${API}/technicians/call-masking-numbers`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/store/locations`, config).catch(() => ({ data: { locations: [] } })),
        axios.get(`${API}/settings/technician-permissions`, config).catch(() => ({ data: {} }))
      ]);
      setDefaultPerms(permsRes.data.default_permissions || {});
      setPermLabels(permsRes.data.permission_labels || {});
      
      setPaymentTypes(paymentRes.data || []);
      setJobSources(sourcesRes.data || []);
      // Defensive: some legacy/seeded services only have `job_type_id`
      setJobTypes((servicesRes.data || []).map(s => ({ ...s, service_id: s.service_id || s.job_type_id })));
      setMaskingNumbers(maskingRes.data || []);
      setStoreLocations((locationsRes.data?.locations || []).filter(l => l.active !== false));
    } catch (error) {
      console.error("Failed to fetch settings data:", error);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/technicians`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTechnicians(response.data);
    } catch (error) {
      // Only show error if it's not an auth error (handled globally)
      if (error.response?.status !== 401) {
        console.error("Failed to fetch technicians:", error);
        toast.error("Failed to load technicians");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      };

      if (editingTechnician) {
        const payload = { ...formData, permissions: formData.permissions === null ? {} : formData.permissions };
        await axios.put(`${API}/technicians/${editingTechnician.technician_id}`, payload, config);
        toast.success("Technician updated");
      } else {
        await axios.post(`${API}/technicians`, formData, config);
        toast.success("Technician added");
      }

      setDialogOpen(false);
      resetForm();
      fetchTechnicians();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => {
          const field = Array.isArray(e.loc) ? e.loc.filter(p => p !== "body").join(".") : "";
          return field ? `${field}: ${e.msg || e}` : (e.msg || e);
        }).join(" • "));
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || JSON.stringify(detail));
      } else {
        toast.error(detail || "Failed to save technician");
      }
    }
  };

  const handleDelete = async (technicianId) => {
    if (!window.confirm("Are you sure you want to remove this technician?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/technicians/${technicianId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Technician removed");
      fetchTechnicians();
    } catch (error) {
      toast.error("Failed to remove technician");
    }
  };

  /* ── Active/Disabled status ── */
  const [disableTarget, setDisableTarget] = useState(null); // { technician, jobs }
  const [reassignTo, setReassignTo] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);

  const authConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const setTechnicianStatus = async (technicianId, status, reassign_to = null) => {
    setStatusBusy(true);
    try {
      const res = await axios.put(`${API}/technicians/${technicianId}/status`, { status, reassign_to }, authConfig());
      if (status === "disabled") {
        const n = res.data?.reassigned_jobs || 0;
        toast.success(n > 0 ? `Technician disabled — ${n} job${n > 1 ? "s" : ""} reassigned` : "Technician disabled");
      } else {
        toast.success("Technician activated");
      }
      setDisableTarget(null);
      setReassignTo("");
      fetchTechnicians();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setStatusBusy(false);
    }
  };

  const handleStatusToggle = async (technician, makeActive) => {
    if (makeActive) {
      setTechnicianStatus(technician.technician_id, "active");
      return;
    }
    try {
      const res = await axios.get(`${API}/technicians/${technician.technician_id}/future-jobs`, authConfig());
      if ((res.data?.count || 0) > 0) {
        setReassignTo("");
        setDisableTarget({ technician, jobs: res.data.jobs });
        return;
      }
    } catch (e) {
      toast.error("Couldn't check upcoming jobs — please try again");
      return;
    }
    setTechnicianStatus(technician.technician_id, "disabled");
  };

  /* ── Pick order (which tech shows first in job-assignment dropdowns) ── */
  const moveTechnician = async (technicianId, dir) => {
    const arr = [...technicians];
    const idx = arr.findIndex(t => t.technician_id === technicianId);
    const swapWith = idx + dir;
    if (idx < 0 || swapWith < 0 || swapWith >= arr.length) return;
    [arr[idx], arr[swapWith]] = [arr[swapWith], arr[idx]];
    setTechnicians(arr);
    try {
      await axios.put(`${API}/technicians/reorder`, { technician_ids: arr.map(t => t.technician_id) }, authConfig());
    } catch (e) {
      toast.error("Failed to save order");
      fetchTechnicians();
    }
  };

  const handleSendInvite = async (technician) => {
    if (!technician.email && !technician.phone) {
      toast.error("Technician needs email or phone to receive invite");
      return;
    }
    // If sending SMS, check phone number is configured
    if (technician.phone && !(await isSmsReadyAsync())) return;
    
    setSendingInvite(technician.technician_id);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/technicians/${technician.technician_id}/invite`, {
        send_sms: !!technician.phone,
        send_email: !!technician.email
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Portal invite sent! " + (technician.phone ? "SMS sent to their phone." : "Email sent."));
      fetchTechnicians();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error("Failed to send invite");
      }
    } finally {
      setSendingInvite(null);
    }
  };

  const handleEdit = (technician) => {
    setEditingTechnician(technician);
    setFormData({
      name: technician.name,
      email: technician.email || "",
      phone: technician.phone || "",
      services: technician.services || [],
      commission_rate: technician.commission_rate || "",
      parts_deduction: technician.parts_deduction || "none",
      charge_processing_fees: technician.charge_processing_fees !== false,
      processing_fees: technician.processing_fees || {},
      commission_by_source: technician.commission_by_source || {},
      commission_by_service: technician.commission_by_service || {},
      call_masking_number: technician.call_masking_number || "",
      custom_assignment_template: technician.custom_assignment_template || "",
      location_ids: technician.location_ids || [],
      permissions: (technician.permissions && Object.keys(technician.permissions).length > 0)
        ? technician.permissions : null,
    });
    setActiveTab("basic");
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTechnician(null);
    setActiveTab("basic");
    setNewPassword("");
    setFormData({
      name: "",
      email: "",
      phone: "",
      services: [],
      commission_rate: "",
      parts_deduction: "none",
      charge_processing_fees: true,
      processing_fees: {},
      commission_by_source: {},
      commission_by_service: {},
      call_masking_number: "",
      custom_assignment_template: "",
      location_ids: [],
      permissions: null,
    });
  };

  const handlePasswordUpdate = async () => {
    if (!editingTechnician || !newPassword) return;
    if (newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    setSavingPassword(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/technicians/${editingTechnician.technician_id}/password`, 
        { password: newPassword },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success("Technician password updated");
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };


  // Helper to update processing fee for a payment type
  const updateProcessingFee = (paymentId, value) => {
    setFormData(prev => ({
      ...prev,
      processing_fees: {
        ...prev.processing_fees,
        [paymentId]: value === "" ? undefined : parseFloat(value) || 0
      }
    }));
  };

  // Helper to update commission by source
  const updateCommissionBySource = (sourceId, value) => {
    setFormData(prev => ({
      ...prev,
      commission_by_source: {
        ...prev.commission_by_source,
        [sourceId]: value === "" ? undefined : parseFloat(value) || 0
      }
    }));
  };

  // Helper to update commission by service
  const updateCommissionByService = (serviceId, value) => {
    setFormData(prev => ({
      ...prev,
      commission_by_service: {
        ...prev.commission_by_service,
        [serviceId]: value === "" ? undefined : parseFloat(value) || 0
      }
    }));
  };

  const getStatusBadge = (accountStatus) => {
    const styles = {
      active: "bg-green-500/20 text-green-400 border-green-500/30",
      invited: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      pending: "bg-gray-500/20 text-gray-400 border-gray-500/30"
    };
    return styles[accountStatus] || styles.pending;
  };

  const fetchUnapprovedJobs = async (techId) => {
    setLoadingUnapproved(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/technicians/${techId}/unapproved-completed-jobs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUnapprovedJobs(res.data.jobs || []);
    } catch (error) {
      toast.error("Failed to load unapproved jobs");
      setUnapprovedJobs([]);
    } finally {
      setLoadingUnapproved(false);
    }
  };

  const fixUnapprovedJobs = async (techId, jobIds = null) => {
    setFixingJobs(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/technicians/${techId}/fix-unapproved-jobs`, 
        { job_ids: jobIds },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(res.data.message);
      fetchUnapprovedJobs(techId);
    } catch (error) {
      toast.error("Failed to fix jobs");
    } finally {
      setFixingJobs(false);
    }
  };

  const getStatusIcon = (accountStatus) => {
    if (accountStatus === "active") return <CheckCircle className="w-3 h-3" />;
    if (accountStatus === "invited") return <Clock className="w-3 h-3" />;
    return null;
  };

  // Stats
  const activeCount = technicians.filter(t => t.account_status === "active").length;
  const pendingCount = technicians.filter(t => t.account_status !== "active").length;

  // Multi-location: filter the grid by assigned location; disabled techs sort last
  const visibleTechnicians = ((isStoreAccount && locationFilter !== "all")
    ? technicians.filter(t => !t.location_ids || t.location_ids.length === 0 || t.location_ids.includes(locationFilter))
    : technicians
  ).slice().sort((a, b) => (a.status === "disabled" ? 1 : 0) - (b.status === "disabled" ? 1 : 0));

  const locationName = (locId) => storeLocations.find(l => l.location_id === locId)?.name || null;

  return (
    <div className="space-y-6" data-testid="technicians-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-foreground font-['Manrope']">Technicians</h2>
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              Unlimited
            </Badge>
          </div>
          <p className="text-muted-foreground">Manage your field technicians - no limits on your plan</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <Button 
            className="btn-primary" 
            data-testid="add-technician-btn"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Technician
          </Button>
          <DialogContent className="bg-background border-border max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-foreground font-['Manrope']">
                {editingTechnician ? "Edit Technician" : "Add Technician"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1">
              <Tabs value={activeTab} onValueChange={(val) => {
                  setActiveTab(val);
                  if (val === "fix-jobs" && editingTechnician) {
                    fetchUnapprovedJobs(editingTechnician.technician_id);
                  }
                }} className="w-full">
                <TabsList className="bg-theme-surface border border-theme p-1 mb-4 flex-wrap h-auto gap-1">
                  <TabsTrigger value="basic" className="data-[state=active]:bg-[#0046BE] data-[state=active]:text-white text-muted-foreground text-xs sm:text-sm">
                    <UserCircle className="w-3.5 h-3.5 mr-1 sm:mr-2" /> Basic
                  </TabsTrigger>
                  <TabsTrigger value="financial" className="data-[state=active]:bg-[#0046BE] data-[state=active]:text-white text-muted-foreground text-xs sm:text-sm">
                    <DollarSign className="w-3.5 h-3.5 mr-1 sm:mr-2" /> Financial
                  </TabsTrigger>
                  {editingTechnician && (
                    <TabsTrigger value="fix-jobs" className="data-[state=active]:bg-[#0046BE] data-[state=active]:text-white text-muted-foreground text-xs sm:text-sm">
                      <Wrench className="w-3.5 h-3.5 mr-1 sm:mr-2" /> Fix Jobs
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="permissions" className="data-[state=active]:bg-[#0046BE] data-[state=active]:text-white text-muted-foreground text-xs sm:text-sm" data-testid="tech-dialog-permissions-tab">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1 sm:mr-2" /> Permissions
                  </TabsTrigger>
                </TabsList>

                {/* Basic Info Tab */}
                <TabsContent value="basic" className="mt-0 overflow-hidden flex-1">
                  <ScrollArea className="h-[400px] pr-2">
                  <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-glass"
                      placeholder="Full name"
                      required
                      data-testid="technician-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-glass"
                        placeholder="email@example.com"
                        data-testid="technician-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Phone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: formatPhoneInput(e.target.value) })}
                        className="input-glass"
                        placeholder="(555) 123-4567"
                        data-testid="technician-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Services This Technician Can Do</Label>
                    {jobTypes.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {jobTypes.map((service) => (
                          <button
                            key={service.service_id}
                            type="button"
                            onClick={() => toggleService(service.service_id)}
                            className={`px-3 py-1 rounded-full text-sm transition-colors ${
                              formData.services.includes(service.service_id)
                                ? "bg-[#FFCC00] text-black"
                                : "bg-theme-hover text-muted-foreground hover:bg-white/20"
                            }`}
                            data-testid={`service-toggle-${service.service_id}`}
                          >
                            {service.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No services configured yet. Add services in Settings first.</p>
                    )}
                  </div>

                  {/* Assigned Locations — multi-location accounts only */}
                  {isStoreAccount && storeLocations.length > 0 && (
                    <div className="space-y-2 pt-4 border-t border-theme">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-emerald-400" />
                        <Label className="text-muted-foreground">Assigned Locations</Label>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {storeLocations.map((loc) => (
                          <button
                            key={loc.location_id}
                            type="button"
                            onClick={() => toggleLocation(loc.location_id)}
                            className={`px-3 py-1 rounded-full text-sm transition-colors ${
                              formData.location_ids.includes(loc.location_id)
                                ? "bg-emerald-500 text-white"
                                : "bg-theme-hover text-muted-foreground hover:bg-white/20"
                            }`}
                            data-testid={`location-toggle-${loc.location_id}`}
                          >
                            {loc.name}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formData.location_ids.length === 0
                          ? "No locations selected — this technician will be available at all locations."
                          : `This technician will only appear and be assignable at the ${formData.location_ids.length} selected location${formData.location_ids.length > 1 ? "s" : ""}.`}
                      </p>
                    </div>
                  )}


                  {/* Call Masking Number Assignment */}
                  {maskingNumbers.length > 0 && (
                    <div className="space-y-2 pt-4 border-t border-theme">
                      <div className="flex items-center gap-2">
                        <PhoneOutgoing className="w-4 h-4 text-blue-400" />
                        <Label className="text-muted-foreground">Assigned Call Masking Number</Label>
                      </div>
                      <Select
                        value={formData.call_masking_number || "none"}
                        onValueChange={(v) => setFormData({ ...formData, call_masking_number: v === "none" ? "" : v })}
                      >
                        <SelectTrigger className="input-glass" data-testid="call-masking-select">
                          <SelectValue placeholder="No masking number assigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No masking number</SelectItem>
                          {maskingNumbers.map(num => (
                            <SelectItem key={num.number_id} value={num.number_id}>
                              {num.phone_number} {num.label ? `(${num.label})` : ""} {num.purpose === "call_masking" ? "— Masking" : ""}{num.owned === false ? " ⚠ NOT ON TWILIO" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        const sel = maskingNumbers.find(n => n.number_id === formData.call_masking_number);
                        return sel && sel.owned === false ? (
                          <p className="text-xs text-red-500 font-medium" data-testid="masking-number-not-owned-warning">
                            ⚠ This number is not active on your Twilio account — calls to it will fail ("number not in service"). Pick a number you own, or complete its transfer/purchase first.
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">Select the masking number to use for this technician's calls.</p>
                        );
                      })()}
                    </div>
                  )}

                  {/* Password Update - Only when editing */}
                  {editingTechnician && (
                    <div className="space-y-2 pt-4 border-t border-theme">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-amber-400" />
                        <Label className="text-muted-foreground">Portal Login Password</Label>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="input-glass"
                          placeholder="Enter new password"
                          data-testid="technician-password-input"
                        />
                        <Button
                          type="button"
                          onClick={handlePasswordUpdate}
                          disabled={savingPassword || !newPassword}
                          className="btn-primary whitespace-nowrap"
                          data-testid="technician-password-save-btn"
                        >
                          {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">Set or change the password this technician uses to log into their portal.</p>
                    </div>
                  )}

                  {/* Custom Assignment SMS Template */}
                  <div className="space-y-2 pt-4 border-t border-theme">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-green-400" />
                      <Label className="text-muted-foreground">Custom Job Assignment SMS Template</Label>
                    </div>
                    <Textarea
                      value={formData.custom_assignment_template}
                      onChange={(e) => setFormData({ ...formData, custom_assignment_template: e.target.value })}
                      className="input-glass min-h-[100px] text-sm"
                      placeholder="Leave empty to use the global default template..."
                      rows={4}
                      data-testid="technician-custom-sms-template"
                    />
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>If set, this template will be used instead of the global template when assigning jobs to this technician.</p>
                      <p className="font-mono text-[10px] leading-relaxed opacity-70">
                        Variables: {"{{job_id}}"} {"{{customer_name}}"} {"{{customer_phone}}"} {"{{job_date}}"} {"{{job_time}}"} {"{{job_city}}"} {"{{job_state}}"} {"{{job_type}}"} {"{{vehicle}}"} {"{{vehicle_year}}"} {"{{vehicle_make}}"} {"{{vehicle_model}}"} {"{{assignment_link}}"}
                      </p>
                    </div>
                  </div>
                  </div>
                  </ScrollArea>
                </TabsContent>

                {/* Financial Settings Tab */}
                <TabsContent value="financial" className="mt-0 overflow-hidden flex-1">
                  <ScrollArea className="h-[400px] pr-2">
                    {/* Default Commission */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-2 text-foreground">
                        <Percent className="w-4 h-4 text-[#FFCC00]" />
                        <Label className="text-sm font-medium">Default Commission Rate</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={formData.commission_rate}
                          onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                          className="input-glass w-24"
                          placeholder="0"
                          data-testid="technician-commission-input"
                        />
                        <span className="text-muted-foreground text-sm">% of job total (used when no specific rate is set)</span>
                      </div>
                    </div>

                    <Separator className="bg-theme-hover my-4" />

                    {/* Parts Deduction Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-theme-surface border border-theme mb-4" data-testid="tech-parts-deduction-toggle">
                      <div className="flex items-center gap-2">
                        <Wrench className="w-4 h-4 text-amber-400" />
                        <div>
                          <Label className="text-sm font-medium text-foreground">Deduct Parts from Commission Base</Label>
                          <p className="text-xs text-muted-foreground">Calculate commission on total minus parts cost instead of full total</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.parts_deduction === "from_total"}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, parts_deduction: checked ? "from_total" : "none" }))}
                      />
                    </div>

                    {/* Charge Processing Fees Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-theme-surface border border-theme mb-4" data-testid="charge-processing-fees-toggle">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-400" />
                        <div>
                          <Label className="text-sm font-medium text-foreground">Charge Processing Fees</Label>
                          <p className="text-xs text-muted-foreground">Deduct payment processing fees from this technician's commission</p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.charge_processing_fees}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, charge_processing_fees: checked }))}
                      />
                    </div>

                    {/* Processing Fees by Payment Method - only show when charging is enabled */}
                    {formData.charge_processing_fees && (
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-2 text-foreground">
                        <CreditCard className="w-4 h-4 text-blue-400" />
                        <Label className="text-sm font-medium">Processing Fees by Payment Method</Label>
                      </div>
                      <p className="text-xs text-muted-foreground">Set the fee percentage to deduct from technician's earnings for each payment method</p>
                      
                      {paymentTypes.length > 0 ? (
                        <div className="space-y-2 mt-3">
                          {paymentTypes.filter(pt => pt.is_active).map((pt) => (
                            <div key={pt.payment_id} className="flex items-center justify-between p-3 rounded-lg bg-theme-surface">
                              <span className="text-muted-foreground">{pt.name}</span>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={formData.processing_fees[pt.payment_id] || ""}
                                  onChange={(e) => updateProcessingFee(pt.payment_id, e.target.value)}
                                  className="input-glass w-20 text-right"
                                  placeholder="0"
                                />
                                <span className="text-muted-foreground text-sm w-6">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm p-3 rounded-lg bg-theme-surface">
                          No payment methods configured. Add them in Settings → Payment Types.
                        </p>
                      )}
                    </div>
                    )}

                    <Separator className="bg-theme-hover my-4" />

                    {/* Commission by Job Source */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-2 text-foreground">
                        <Briefcase className="w-4 h-4 text-purple-400" />
                        <Label className="text-sm font-medium">Commission Rate by Job Source</Label>
                      </div>
                      <p className="text-xs text-muted-foreground">Override default commission for specific job sources</p>
                      
                      {jobSources.length > 0 ? (
                        <div className="space-y-2 mt-3">
                          {jobSources.map((source) => (
                            <div key={source.source_id} className="flex items-center justify-between p-3 rounded-lg bg-theme-surface">
                              <div>
                                <span className="text-muted-foreground">{source.name}</span>
                                {source.commission_value > 0 && (
                                  <span className="text-muted-foreground text-xs ml-2">
                                    (Source fee: {source.commission_type === "percentage" ? `${source.commission_value}%` : `$${source.commission_value}`})
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={formData.commission_by_source[source.source_id] || ""}
                                  onChange={(e) => updateCommissionBySource(source.source_id, e.target.value)}
                                  className="input-glass w-20 text-right"
                                  placeholder="Default"
                                />
                                <span className="text-muted-foreground text-sm w-6">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm p-3 rounded-lg bg-theme-surface">
                          No job sources configured. Add them in Settings → Job Sources.
                        </p>
                      )}
                    </div>

                    <Separator className="bg-theme-hover my-4" />

                    {/* Commission by Service/Job Type */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-foreground">
                        <DollarSign className="w-4 h-4 text-green-400" />
                        <Label className="text-sm font-medium">Commission Rate by Service Type</Label>
                      </div>
                      <p className="text-xs text-muted-foreground">Override default commission for specific service types</p>
                      
                      {jobTypes.length > 0 ? (
                        <div className="space-y-2 mt-3">
                          {jobTypes.map((service) => (
                            <div key={service.service_id} className="flex items-center justify-between p-3 rounded-lg bg-theme-surface">
                              <span className="text-muted-foreground">{service.name}</span>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={formData.commission_by_service[service.service_id] || ""}
                                  onChange={(e) => updateCommissionByService(service.service_id, e.target.value)}
                                  className="input-glass w-20 text-right"
                                  placeholder="Default"
                                />
                                <span className="text-muted-foreground text-sm w-6">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm p-3 rounded-lg bg-theme-surface">
                          No services configured. Add them in Settings → Services.
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Fix Jobs Tab */}
                {editingTechnician && (
                  <TabsContent value="fix-jobs" className="space-y-4 mt-0">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Completed jobs where this technician's assignment was never approved. Click "Fix All" to mark them as accepted, or fix individually.
                      </p>
                    </div>
                    {loadingUnapproved ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                      </div>
                    ) : unapprovedJobs.length === 0 ? (
                      <div className="text-center py-10 rounded-xl bg-theme-surface border border-theme">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                        <p className="text-muted-foreground text-sm">All completed jobs are properly approved</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-foreground font-medium">{unapprovedJobs.length} job(s) need fixing</p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => fixUnapprovedJobs(editingTechnician.technician_id)}
                            disabled={fixingJobs}
                            className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/25"
                            data-testid="fix-all-jobs-btn"
                          >
                            {fixingJobs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            Fix All
                          </Button>
                        </div>
                        <ScrollArea className="h-[350px]">
                          <div className="space-y-2 pr-3">
                            {unapprovedJobs.map((job) => (
                              <div key={job.job_id} className="flex items-center justify-between p-3 rounded-lg bg-theme-surface border border-theme" data-testid={`unapproved-job-${job.job_id}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-foreground truncate">{job.title}</p>
                                    {job.job_display_id && <span className="text-[10px] font-mono text-muted-foreground/50 bg-muted px-1.5 py-0.5 rounded">{job.job_display_id}</span>}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                    <span>{job.customer_name}</span>
                                    {job.total_price > 0 && <span className="text-emerald-400">${Number(job.total_price).toFixed(2)}</span>}
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => fixUnapprovedJobs(editingTechnician.technician_id, [job.job_id])}
                                  disabled={fixingJobs}
                                  className="text-xs text-muted-foreground hover:text-emerald-400"
                                  data-testid={`fix-job-${job.job_id}`}
                                >
                                  {fixingJobs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                                </Button>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </>
                    )}
                  </TabsContent>
                )}

                {/* Per-Technician Permissions Tab */}
                <TabsContent value="permissions" className="mt-0 overflow-hidden flex-1">
                  <ScrollArea className="h-[400px] pr-2">
                    <div className="space-y-4">
                      <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-theme/40 bg-muted/20 cursor-pointer">
                        <div>
                          <p className="text-sm font-medium text-foreground">Use account default permissions</p>
                          <p className="text-[11px] text-muted-foreground">
                            Manage defaults on the Technicians page → Permissions tab
                          </p>
                        </div>
                        <Switch
                          checked={formData.permissions === null}
                          onCheckedChange={(v) => setFormData({
                            ...formData,
                            permissions: v ? null : { ...defaultPerms },
                          })}
                          data-testid="use-default-permissions-toggle"
                        />
                      </label>
                      {formData.permissions !== null && (
                        <PermissionToggleGrid
                          values={formData.permissions}
                          labels={permLabels}
                          onChange={(key, v) => setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, [key]: v },
                          })}
                          testPrefix="tech-perm"
                        />
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-3 pt-4 border-t border-theme mt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-muted-foreground">
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="technician-submit-btn">
                  {editingTechnician ? "Update" : "Add"} Technician
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Page Tabs: Technicians | Permissions & Privacy */}
      <div className="flex gap-1 p-1 rounded-xl w-fit bg-muted/40 border border-theme" data-testid="technicians-page-tabs">
        <button
          onClick={() => setPageTab("technicians")}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${pageTab === "technicians" ? "bg-[#0046BE] text-white" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="page-tab-technicians"
        >
          <Users className="w-3.5 h-3.5" /> Technicians
        </button>
        <button
          onClick={() => setPageTab("permissions")}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${pageTab === "permissions" ? "bg-[#0046BE] text-white" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="page-tab-permissions"
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Permissions & Privacy
        </button>
      </div>

      {pageTab === "permissions" && <TechPermissionsTab />}

      {pageTab === "technicians" && (<>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <Wrench className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{technicians.length}</p>
              <p className="text-muted-foreground text-sm">Total Technicians</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-500/20">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{activeCount}</p>
              <p className="text-muted-foreground text-sm">Active (Portal Access)</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-yellow-500/20">
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{pendingCount}</p>
              <p className="text-muted-foreground text-sm">Pending Activation</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Multi-location: filter technicians by assigned location */}
      {isStoreAccount && storeLocations.length > 1 && (
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-56 input-glass" data-testid="technician-location-filter">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {storeLocations.map(loc => (
                <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Technicians Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading technicians...</div>
      ) : visibleTechnicians.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleTechnicians.map((technician, index) => (
            <Card key={technician.technician_id} className={`glass-card border-theme group ${technician.status === "disabled" ? "opacity-60" : ""}`} data-testid={`technician-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-14 w-14 border-2 border-theme">
                      <AvatarFallback className="bg-[#0046BE] text-white text-lg">
                        {technician.name.split(" ").map(n => n[0]).join("").toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-foreground">{technician.name}</h3>
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 border mt-1">
                        Technician
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={`${getStatusBadge(technician.account_status)} border capitalize flex items-center gap-1`}>
                      {getStatusIcon(technician.account_status)}
                      {technician.account_status || "pending"}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${technician.status === "disabled" ? "text-red-400" : "text-emerald-400"}`}>
                        {technician.status === "disabled" ? "Disabled" : "Active"}
                      </span>
                      <Switch
                        checked={technician.status !== "disabled"}
                        onCheckedChange={(v) => handleStatusToggle(technician, v)}
                        disabled={statusBusy}
                        data-testid={`tech-status-toggle-${index}`}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {technician.email && (
                    <p className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Mail className="w-4 h-4" /> {technician.email}
                    </p>
                  )}
                  {technician.phone && (
                    <p className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Phone className="w-4 h-4" /> {technician.phone}
                    </p>
                  )}
                  {technician.thumbtack_sync_status && (
                    <p className="flex items-center" data-testid={`tech-thumbtack-sync-${index}`}>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                          technician.thumbtack_sync_status === "synced"
                            ? "bg-teal-500/15 text-teal-500 border-teal-500/30"
                            : technician.thumbtack_sync_status === "sync_failed"
                              ? "bg-red-500/15 text-red-400 border-red-500/30"
                              : "bg-muted/40 text-muted-foreground border-theme"
                        }`}
                        title={technician.thumbtack_sync_error || ""}
                      >
                        {technician.thumbtack_sync_status === "synced"
                          ? "Synced with Thumbtack"
                          : technician.thumbtack_sync_status === "sync_failed"
                            ? "Thumbtack sync failed"
                            : "Not synced with Thumbtack"}
                      </span>
                    </p>
                  )}
                  {isStoreAccount && (
                    <p className="flex items-center gap-2 text-muted-foreground text-sm" data-testid={`tech-locations-${index}`}>
                      <MapPin className="w-4 h-4 shrink-0" />
                      <span className="truncate">
                        {(technician.location_ids && technician.location_ids.length > 0)
                          ? technician.location_ids.map(locationName).filter(Boolean).join(", ") || "Assigned locations"
                          : "All locations"}
                      </span>
                    </p>
                  )}
                </div>

                {technician.services && technician.services.length > 0 && (() => {
                  const expanded = !!expandedServices[technician.technician_id];
                  const VISIBLE = 4;
                  const total = technician.services.length;
                  const list = technician.services
                    .map((sid) => {
                      const s = jobTypes.find((jt) => jt.service_id === sid);
                      return s?.name || sid;
                    })
                    .filter(Boolean);
                  const visible = expanded ? list : list.slice(0, VISIBLE);
                  const hidden = total - VISIBLE;

                  return (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <Wrench className="w-3.5 h-3.5 text-muted-foreground/70" />
                          <p className="text-xs font-medium text-muted-foreground">Services</p>
                          <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-500/10 text-blue-500 border border-blue-500/20">
                            {total}
                          </span>
                        </div>
                        {hidden > 0 && (
                          <button
                            onClick={() =>
                              setExpandedServices((prev) => ({ ...prev, [technician.technician_id]: !expanded }))
                            }
                            className="text-[11px] font-medium text-blue-500 hover:text-blue-400 flex items-center gap-0.5"
                            data-testid={`tech-services-toggle-${index}`}
                          >
                            {expanded ? (
                              <>Show less <ChevronUp className="w-3 h-3" /></>
                            ) : (
                              <>Show all <ChevronDown className="w-3 h-3" /></>
                            )}
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {visible.map((name, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 text-[11px] rounded-full bg-theme-surface text-foreground/80 border border-theme/50 truncate max-w-[140px]"
                            title={name}
                          >
                            {name}
                          </span>
                        ))}
                        {!expanded && hidden > 0 && (
                          <button
                            onClick={() =>
                              setExpandedServices((prev) => ({ ...prev, [technician.technician_id]: true }))
                            }
                            className="px-2 py-0.5 text-[11px] rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/20 hover:bg-blue-500/15"
                            data-testid={`tech-services-more-${index}`}
                          >
                            +{hidden} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="flex items-center justify-between pt-4 border-t border-theme">
                  <div className="text-sm flex items-center gap-4">
                    <div>
                      <span className="text-muted-foreground">Jobs: </span>
                      <span className="text-foreground">{technician.jobs_completed || 0}</span>
                    </div>
                    {technician.commission_rate > 0 && (
                      <div>
                        <span className="text-muted-foreground">Commission: </span>
                        <span className="text-[#FFCC00]">{technician.commission_rate}%</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => moveTechnician(technician.technician_id, -1)}
                      className="text-muted-foreground hover:text-foreground hover:bg-theme-hover h-8 w-8"
                      title="Move up in job-assignment pick order"
                      data-testid={`move-up-technician-${index}`}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => moveTechnician(technician.technician_id, 1)}
                      className="text-muted-foreground hover:text-foreground hover:bg-theme-hover h-8 w-8"
                      title="Move down in job-assignment pick order"
                      data-testid={`move-down-technician-${index}`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    {technician.account_status !== "active" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSendInvite(technician)}
                        disabled={sendingInvite === technician.technician_id}
                        className="text-[#0046BE] hover:text-[#0046BE] hover:bg-[#0046BE]/10 h-8 w-8"
                        title="Send portal invite"
                        data-testid={`invite-technician-${index}`}
                      >
                        {sendingInvite === technician.technician_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(technician)}
                      className="text-muted-foreground hover:text-white hover:bg-theme-hover h-8 w-8"
                      data-testid={`edit-technician-${index}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(technician.technician_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8"
                      data-testid={`delete-technician-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="glass-card border-theme">
          <CardContent className="text-center py-12">
            <Wrench className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-muted-foreground">No technicians yet</p>
            <p className="text-muted-foreground text-sm mb-4">Add technicians to assign them to jobs</p>
            <Button 
              className="mt-4 btn-primary" 
              onClick={() => setDialogOpen(true)} 
              data-testid="empty-add-technician-btn"
            >
              Add Your First Technician
            </Button>
          </CardContent>
        </Card>
      )}
      </>)}
      {/* Disable warning: technician has upcoming jobs */}
      <Dialog open={!!disableTarget} onOpenChange={(o) => { if (!o) { setDisableTarget(null); setReassignTo(""); } }}>
        <DialogContent className="glass-card border-theme max-w-lg" data-testid="disable-tech-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              {disableTarget?.technician?.name} has upcoming jobs
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This technician is assigned to {disableTarget?.jobs?.length} upcoming job{disableTarget?.jobs?.length > 1 ? "s" : ""}.
              You can reassign them to an active technician, or disable anyway and reassign later from the Jobs page.
            </p>
            <div className="max-h-44 overflow-y-auto space-y-1.5 pr-1">
              {(disableTarget?.jobs || []).map((j) => (
                <div key={j.job_id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-theme-hover text-sm">
                  <span className="text-foreground truncate">{j.customer_name || j.title || j.service_name || j.job_id}</span>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {(j.scheduled_date || "").slice(0, 10) || "No date"}{j.scheduled_time ? ` ${j.scheduled_time}` : ""}
                  </span>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">Reassign these jobs to</Label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger className="input-glass" data-testid="reassign-tech-select">
                  <SelectValue placeholder="Select an active technician (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {technicians
                    .filter(t => t.status !== "disabled" && t.technician_id !== disableTarget?.technician?.technician_id)
                    .map(t => (
                      <SelectItem key={t.technician_id} value={t.technician_id}>{t.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button
                variant="outline"
                className="flex-1 border-theme"
                onClick={() => { setDisableTarget(null); setReassignTo(""); }}
                disabled={statusBusy}
                data-testid="disable-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-red-500/40 text-red-400 hover:bg-red-500/10"
                onClick={() => setTechnicianStatus(disableTarget.technician.technician_id, "disabled")}
                disabled={statusBusy}
                data-testid="disable-anyway-btn"
              >
                Disable anyway
              </Button>
              <Button
                className="flex-1 btn-primary"
                onClick={() => setTechnicianStatus(disableTarget.technician.technician_id, "disabled", reassignTo)}
                disabled={statusBusy || !reassignTo}
                data-testid="reassign-and-disable-btn"
              >
                Reassign & Disable
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TechniciansPage;
